import {
  BoxProps,
  TableProps,
  TableRowProps,
  TextFieldProps,
  TableCellProps,
} from '@mui/material';
import { MRT_TableState } from 'material-react-table';

import { AlarmDefinition } from 'types/types';

export const notAvailableData = 'N/A';

export const capitalizeFirstLetter = (value: string): string => {
  if (!value && value.length > 2) return '';
  return value.charAt(0).toUpperCase() + value.slice(1);
};

export const replaceEmptyValues = (
  data: any[],
  valueToReplace = 'N/A'
): { [key: string]: any }[] => {
  return data.map((obj) =>
    Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [
        key,
        value === null || value === '' || value === undefined || value === 0
          ? valueToReplace
          : value,
      ])
    )
  );
};

export const constructBreachingPeriodsValue = (
  definition: AlarmDefinition,
  valueToReplace = 'N/A'
) => {
  const numberOfBreachedInterval =
    definition.execution_rules_serialized?.instancesCreationPeriodCriteria
      ?.numberOfBreachedInterval;
  const numberOfLatestIntervals =
    definition.execution_rules_serialized?.instancesCreationPeriodCriteria
      ?.numberOfLatestIntervals;
  if (
    numberOfBreachedInterval &&
    numberOfBreachedInterval > 0 &&
    numberOfLatestIntervals &&
    numberOfLatestIntervals > 0
  ) {
    return `Must Breach ${numberOfBreachedInterval} out of the latest ${numberOfLatestIntervals} instances`;
  }
  return valueToReplace;
};

export const formatAlarmDefinitionForGrid = (
  data: AlarmDefinition[],
  valueToReplace = 'N/A'
) => {
  let formattedArray = replaceEmptyValues(
    data,
    valueToReplace
  ) as AlarmDefinition[];

  formattedArray = formattedArray.map((el) => {
    if (el.execution_rules_serialized?.instancesCreationPeriodCriteria) {
      el.breachingPeriods = constructBreachingPeriodsValue(el, valueToReplace);
    } else {
      el.breachingPeriods = valueToReplace;
    }
    return el;
  });

  return formattedArray;
};

/* Alarm List View Table Column Options */
const defaultRowPerPage = 10;

export const tableInitialState: Partial<MRT_TableState<AlarmDefinition>> = {
  grouping: ['execution_group'],
  pagination: { pageIndex: 0, pageSize: defaultRowPerPage },
  density: 'compact',
};

export const topToolbarProps: BoxProps = {
  sx: {
    '& .MuiAlert-root': {
      color: 'var(--color-contrast)',
      backgroundColor: 'var(--color-accent-shade-secondary)',
    },
    '& .Mui-focused fieldset': {
      borderColor: 'var(--color-primary-accent) !important',
    },
    '& .Mui-ToolbarDropZone': {
      border: '2px dashed var(--color-primary-accent)',
      backgroundColor: 'rgba(255, 87, 34, 0.1)',
    },
    '& .MuiAlert-message': {
      maxWidth: 'calc(100% - 23rem) !important',
    },
  },
};

export const resizeDividerClassName: any = {
  '& hr.MuiDivider-root.MuiDivider-fullWidth.MuiDivider-vertical.MuiDivider-flexItem.Mui-TableHeadCell-ResizeHandle-Divider:active':
    {
      borderColor: 'var(--color-primary-accent)',
    },
};

export const tableHeadCellProps: TableCellProps = {
  sx: {
    ...resizeDividerClassName,
    '& hr.MuiDivider-root.MuiDivider-fullWidth.MuiDivider-vertical.MuiDivider-flexItem.Mui-TableHeadCell-ResizeHandle-Divider:active':
      {
        borderColor: 'var(--color-primary-accent)',
      },
    '& .MuiTableSortLabel-root': { zIndex: 10 },
    '& button.MuiButtonBase-root.MuiIconButton-root.MuiIconButton-sizeSmall[draggable=true]':
      {
        position: 'absolute',
        left: 0,
        right: 0,
        width: '100%',
        opacity: 0,
      },
    justifyContent: 'center',
  },
};

export const tableBodyRowProps: TableRowProps = {
  hover: false,
  sx: {
    height: '3.5rem',
  },
};

export const expandColumnTableHeadCellProps: TableCellProps = {
  sx: {
    ...resizeDividerClassName,
    width: 'auto',
    // wid.eSpace: 'nowrap', // Prevents wrapping if the header text is long
  },
};

export const searchFilterTextFieldProps: TextFieldProps = {
  sx: {
    '& .MuiInput-root::after': {
      borderColor: 'var(--color-primary-accent) !important',
    },
  },
};

export const actionsColumnHeadCellProps: TableCellProps = {
  ...tableHeadCellProps,
  align: 'center',
};

export const actionsColumnBodyCellProps: TableCellProps = {
  style: {
    paddingLeft: '1rem',
  },
  align: 'center',
};

export const tableProps: TableProps = {
  sx: {
    tableLayout: 'fixed',
    '& .MuiTableCell-head': {
      justifyContent: 'center',
    },
  },
};

/*End Alarm List View Table Column Options*/
